import parse from 'html-react-parser';
import { useSelector } from 'react-redux';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import pushToGTM, {
	redirectToProductDatasheetGA4,
	redirectToProductPageTagGA4,
	redirectToWebDesignerGA4
} from '../../utils/analytics/gtm';
import theme from '../../styles/theme';
import { URL } from '../../utils/constants/general';
import { CIDS } from './constants';
import Link from '../Link';
import React from 'react';
import i18next from 'i18next';
import PartSuitabilityIndicator from './PartSuitabilityIndicator';
import RedirectButtonRound from './RedirectButtonRound/index';
import SnapMagicViewer from './SnapMagicViewer';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../../styles/constants';
import * as adob from '../../utils/analytics/adobe/adob';
import i18n, { getCurrentLanguage } from '../../utils/i18n';
import SimulationsCell from './SimulationsCell';

export function cleanHTMLtags(columnLabel) {
	const regex = new RegExp('(<sub>|</sub>|<sup>|</sup>)', 'g');
	return columnLabel.replaceAll(regex, '');
}

export function RenderCustomCellComponent(cid, value, partSuitability, row, domain, fullURL, path) {
	const { t } = useTranslation(['main', 'category']);
	const useInputParameters = useSelector(state => state.wdValidator.inputParameters);
	const inputParametersInURL = queryString.stringify(useInputParameters);
	const similarParts = useSelector(state => state.filters.similarParts.parts);

	// The Tools (SnapMagic) column has no backing product field, so its `value`
	// is always undefined — exempt it from every empty-value early return so the
	// viewer still mounts.
	if (
		cid !== CIDS.TOOLS &&
		((value === null && cid !== CIDS.DATASHEET && cid !== CIDS.SIMULATIONS) || value === undefined || value === '')
	) {
		return null;
	} else if (cid === CIDS.PART_SUITABILITY) {
		return (
			<PartSuitabilityIndicator
				partSuitability={partSuitability}
				label={t('main:components.productsTable.partSuitability')}
				highlightColor={similarParts.includes(row[CIDS.PART_NUMBER]) ? COLORS.PRIMARY : ''}
			/>
		);
	} else if (cid === CIDS.GENERAL_PART_NUMBER) {
		const productPagelink = URL.PRODUCT_PAGE + value;
		const partNumber = row[CIDS.PART_NUMBER];
		const trackingParameters = {
			redirectLink: productPagelink,
			partNumber: value,
			buttonName: value,
			segment: adob.segment,
			fullURL: fullURL,
			path: path,
			breadcrumbs: adob.breadcrumbs,
			logEventType: adob.logEventType.PRODUCTLOG,
			partRankIndex: row.partRankIndex,
			opn: row[CIDS.PART_NUMBER]
		};
		const redirectLink =
			domain +
			'/design/tools-software/product-recommendation-tools-plus/linksRedirect?' +
			queryString.stringify(trackingParameters);
		return (
			<div style={{ minWidth: '160px' }}>
				<Link
					tooltipTitle={t('main:components.productsTable.productWPN.tooltip')}
					noWrap
					variant="body2"
					color={similarParts.includes(row[CIDS.PART_NUMBER]) ? COLORS.PRIMARY : ''}
					href={redirectLink}
					onClick={() => {
						pushToGTM(redirectToProductPageTagGA4());
					}}
					target="_blank">
					{value}
				</Link>
				<br />
				<span style={{ whiteSpace: 'nowrap' }}>{partNumber}</span>
			</div>
		);
	} else if (cid === CIDS.DATASHEET) {
		const datasheetTitle = value
			? t('main:components.productsTable.datasheet.open')
			: t('main:components.productsTable.datasheet.request');
		const datasheetLink = value ? URL.DATASHEET + value.toLowerCase() : URL.SALES_SUPPORT;
		const trackingParameters = {
			redirectLink: datasheetLink,
			partNumber: value,
			buttonName: datasheetTitle,
			segment: adob.segment,
			fullURL: fullURL,
			path: path,
			breadcrumbs: adob.breadcrumbs,
			logEventType: adob.logEventType.DATASHEETLOG,
			partRankIndex: row.partRankIndex,
			opn: row[CIDS.PART_NUMBER]
		};
		const redirectLink =
			domain +
			'/design/tools-software/product-recommendation-tools-plus/linksRedirect?' +
			queryString.stringify(trackingParameters);

		return (
			<Link
				tooltipTitle={datasheetTitle}
				color={value ? 'primary' : 'textSecondary'}
				target="_blank"
				href={redirectLink}
				onClick={() => {
					pushToGTM(redirectToProductDatasheetGA4(value));
				}}>
				<DescriptionOutlinedIcon />
				<div>{datasheetTitle}</div>
			</Link>
		);
	} else if (cid === CIDS.TOOLS) {
		const lang = domain.includes('.cn') ? 'cn' : 'en';
		const partNumber = row[CIDS.PART_NUMBER];
		return <SnapMagicViewer partNumber={partNumber} lang={lang} />;
	} else if (cid === CIDS.PRICE && value !== '-') {
		return '$' + value;
	} else if (cid === CIDS.SIMULATIONS) {
		return <SimulationsCell row={row} path={path} />;
	} else if (cid === CIDS.DESCRIPTION) {
		const productPagelink = URL.PRODUCT_PAGE + row.gpn;
		const trackingParameters = {
			redirectLink: productPagelink,
			partNumber: row.gpn,
			buttonName: row.gpn,
			segment: adob.segment,
			fullURL: fullURL,
			path: path,
			breadcrumbs: adob.breadcrumbs,
			logEventType: adob.logEventType.PRODUCTLOG,
			partRankIndex: row.partRankIndex,
			opn: row[CIDS.PART_NUMBER]
		};
		const redirectLink =
			domain +
			'/design/tools-software/product-recommendation-tools-plus/linksRedirect?' +
			queryString.stringify(trackingParameters);
		return (
			<Link
				tooltipTitle={t('main:components.productsTable.productWPN.tooltip')}
				variant="body2"
				color={similarParts.includes(row[CIDS.PART_NUMBER]) ? COLORS.PRIMARY : ''}
				href={redirectLink}
				onClick={() => {
					pushToGTM(redirectToProductPageTagGA4());
				}}
				target="_blank">
				<p style={{ width: 200, margin: 0 }}>{parse(value)}</p>
			</Link>
		);
	} else if (typeof value === 'string') {
		const valueLength = value.length;

		if (valueLength > 49) {
			return <p style={{ minWidth: 170, margin: 0 }}>{parse(value)}</p>;
		} else if (valueLength > 19) {
			return <p style={{ minWidth: 110, margin: 0 }}>{parse(value)}</p>;
		}

		return parse(value);
	} else if (typeof value === 'boolean') {
		return value ? (
			<CheckIcon
				fontSize="small"
				style={{
					marginLeft: theme.spacing(1),
					color: theme.palette.text.secondary
				}}
			/>
		) : (
			<ClearIcon
				fontSize="small"
				style={{
					marginLeft: theme.spacing(1),
					color: theme.palette.text.secondary
				}}
			/>
		);
	} else if (cid === CIDS.WEB_DESIGNER) {
		return (
			<Link
				href={
					URL.DESIGN_TOOLS +
					inputParametersInURL +
					'&topology=' +
					row[CIDS.WD_TOPOLOGY] +
					'&ic=' +
					row[CIDS.PART_NUMBER]
				}
				target="_blank"
				style={{ textDecoration: 'none' }}
				onClick={() => pushToGTM(redirectToWebDesignerGA4())}>
				<RedirectButtonRound>WD+</RedirectButtonRound>
			</Link>
		);
	} else {
		return value;
	}
}

export function RenderCustomHeadLabel(appliedStateOfQuickFilter, displayModeIsWPN, originalColumns, actMDTColumn) {
	const keyPrefix = 'filters';
	const supplyTypeState = appliedStateOfQuickFilter?.pwcv?.value?.toString()?.substring(0, 2);
	const originalColumn = originalColumns.find(col => col.cid === actMDTColumn.name);
	const lang = getCurrentLanguage();
	/* - <button> changes to <span> when OPN mode is changed to WPN because of disabled sorting
       - to keep styling consistent, we imitate MUI <button> by adding styled <div>
    */

	let columnLabel;
	if (lang === 'cn' && i18next.exists(`${keyPrefix}.${originalColumn.cid}`)) {
		columnLabel = displayModeIsWPN
			? '<div style="display: inline-block; padding: 1px 4px; margin-left: -8px; margin-right: 8px; text-align: center; line-height: 1.75;">' +
			  i18n.t(`${keyPrefix}.${originalColumn.cid}`) +
			  '</div>'
			: i18n.t(`${keyPrefix}.${originalColumn.cid}`);
	} else {
		columnLabel = displayModeIsWPN
			? '<div style="display: inline-block; padding: 1px 4px; margin-left: -8px; margin-right: 8px; text-align: center; line-height: 1.75;">' +
			  originalColumn.label +
			  '</div>'
			: originalColumn.label;
	}

	if (originalColumn.cid === 'vim' && supplyTypeState === 'DC') {
		columnLabel = 'V<sub>IN<sub>DC</sub> (max)</sub> (V)';
	} else if (originalColumn.cid === 'vim' && supplyTypeState === 'AC') {
		columnLabel = 'V<sub>IN<sub>RMS</sub> (max)</sub> (V)';
	} else if (originalColumn.cid === 'vin' && supplyTypeState === 'DC') {
		columnLabel = 'V<sub>IN<sub>DC</sub> (min)</sub> (V)';
	} else if (originalColumn.cid === 'vin' && supplyTypeState === 'AC') {
		columnLabel = 'V<sub>IN<sub>RMS</sub> (min)</sub> (V)';
	}

	return originalColumn ? parse(columnLabel) : actMDTColumn.label;
}
